import { Type } from '@sinclair/typebox';
import type { RouteContract } from '@spfn/core/route';

/**
 * CMS Labels Contracts
 *
 * 라벨 메타데이터 관리 API
 */

/**
 * GET /_cms/labels - 라벨 목록 조회
 */
export const getLabelsContract = {
    method: 'GET' as const,
    path: '/_cms/labels',
    query: Type.Object({
        section: Type.Optional(Type.String({ description: '섹션으로 필터링 (예: home, why-futureplay)' })),
        includeDefaultValues: Type.Optional(Type.Boolean({ description: '기본값 포함 여부' }))
    }),
    response: Type.Object({
        labels: Type.Array(Type.Object({
            id: Type.Number(),
            key: Type.String(),
            section: Type.String(),
            type: Type.String(),
            description: Type.Union([Type.String(), Type.Null()], { description: '라벨 설명' }),
            publishedVersion: Type.Union([Type.Number(), Type.Null()]),
            createdBy: Type.Union([Type.String(), Type.Null()]),
            createdAt: Type.String(),
            updatedAt: Type.String(),
            defaultValue: Type.Optional(Type.Any({ description: '라벨 정의 파일의 기본값' }))
        })),
        total: Type.Number()
    })
} as const satisfies RouteContract;

/**
 * POST /_cms/labels - 새 라벨 생성
 */
export const createLabelContract = {
    method: 'POST' as const,
    path: '/_cms/labels',
    body: Type.Object({
        key: Type.String({
            description: '고유 키 (예: home.hero.title)',
            pattern: '^[a-z0-9-]+\\.[a-z0-9-]+\\.[a-z0-9-]+$'
        }),
        section: Type.String({
            description: '섹션 이름 (예: home, why-futureplay)',
            pattern: '^[a-z0-9-]+$'
        }),
        type: Type.Union([
            Type.Literal('text'),
            Type.Literal('image'),
            Type.Literal('video'),
            Type.Literal('file'),
            Type.Literal('object')
        ], { description: '값 타입' }),
        createdBy: Type.Optional(Type.String({ description: '생성자 ID' }))
    }),
    response: Type.Union([
        Type.Object({
            id: Type.Number(),
            key: Type.String(),
            section: Type.String(),
            type: Type.String(),
            publishedVersion: Type.Union([Type.Number(), Type.Null()]),
            createdBy: Type.Union([Type.String(), Type.Null()]),
            createdAt: Type.String(),
            updatedAt: Type.String()
        }),
        Type.Object({
            error: Type.String(),
            key: Type.Optional(Type.String())
        })
    ])
} as const satisfies RouteContract;

/**
 * GET /_cms/labels/:id - 라벨 단건 조회
 */
export const getLabelContract = {
    method: 'GET' as const,
    path: '/_cms/labels/:id',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    response: Type.Union([
        Type.Object({
            id: Type.Number(),
            key: Type.String(),
            section: Type.String(),
            type: Type.String(),
            description: Type.Union([Type.String(), Type.Null()]),
            publishedVersion: Type.Union([Type.Number(), Type.Null()]),
            createdBy: Type.Union([Type.String(), Type.Null()]),
            createdAt: Type.String(),
            updatedAt: Type.String()
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * PATCH /_cms/labels/:id - 라벨 메타데이터 수정
 */
export const updateLabelContract = {
    method: 'PATCH' as const,
    path: '/_cms/labels/:id',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    body: Type.Object({
        section: Type.Optional(Type.String({ description: '섹션 변경' })),
        type: Type.Optional(Type.Union([
            Type.Literal('text'),
            Type.Literal('image'),
            Type.Literal('video'),
            Type.Literal('file'),
            Type.Literal('object')
        ]))
    }),
    response: Type.Union([
        Type.Object({
            id: Type.Number(),
            key: Type.String(),
            section: Type.String(),
            type: Type.String(),
            description: Type.Union([Type.String(), Type.Null()]),
            publishedVersion: Type.Union([Type.Number(), Type.Null()]),
            createdBy: Type.Union([Type.String(), Type.Null()]),
            createdAt: Type.String(),
            updatedAt: Type.String()
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * DELETE /_cms/labels/:id - 라벨 삭제
 */
export const deleteLabelContract = {
    method: 'DELETE' as const,
    path: '/_cms/labels/:id',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    response: Type.Union([
        Type.Object({
            success: Type.Boolean(),
            id: Type.Number()
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * GET /_cms/labels/by-key/:key - Key로 라벨 조회
 */
export const getLabelByKeyContract = {
    method: 'GET' as const,
    path: '/_cms/labels/by-key/:key',
    params: Type.Object({
        key: Type.String({ description: '라벨 Key (예: home.hero.title)' })
    }),
    response: Type.Union([
        Type.Object({
            id: Type.Number(),
            key: Type.String(),
            section: Type.String(),
            type: Type.String(),
            description: Type.Union([Type.String(), Type.Null()]),
            publishedVersion: Type.Union([Type.Number(), Type.Null()]),
            createdBy: Type.Union([Type.String(), Type.Null()]),
            createdAt: Type.String(),
            updatedAt: Type.String()
        }),
        Type.Object({
            error: Type.String(),
            key: Type.Optional(Type.String())
        })
    ])
} as const satisfies RouteContract;

/**
 * POST /_cms/labels/:id/publish - 라벨 발행 (Draft → Published)
 */
export const publishLabelContract = {
    method: 'POST' as const,
    path: '/_cms/labels/:id/publish',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    body: Type.Object({
        notes: Type.Optional(Type.String({ description: '발행 노트 (버전 설명)' })),
        publishedBy: Type.Optional(Type.String({ description: '발행자 ID' }))
    }),
    response: Type.Union([
        Type.Object({
            success: Type.Boolean(),
            id: Type.Number(),
            version: Type.Number(),
            message: Type.String()
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * GET /_cms/labels/:id/admin - 관리자용 라벨 조회 (Draft + Published + Status)
 */
export const getAdminLabelContract = {
    method: 'GET' as const,
    path: '/_cms/labels/:id/admin',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    response: Type.Union([
        Type.Object({
            label: Type.Object({
                id: Type.Number(),
                key: Type.String(),
                section: Type.String(),
                type: Type.String(),
                description: Type.Union([Type.String(), Type.Null()]),
                publishedVersion: Type.Union([Type.Number(), Type.Null()]),
                createdBy: Type.Union([Type.String(), Type.Null()]),
                createdAt: Type.String(),
                updatedAt: Type.String()
            }),
            draft: Type.Array(Type.Object({
                id: Type.Number(),
                labelId: Type.Number(),
                version: Type.Null(),
                locale: Type.String(),
                breakpoint: Type.Union([Type.String(), Type.Null()]),
                value: Type.Any(),
                createdAt: Type.String()
            })),
            published: Type.Array(Type.Object({
                id: Type.Number(),
                labelId: Type.Number(),
                version: Type.Number(),
                locale: Type.String(),
                breakpoint: Type.Union([Type.String(), Type.Null()]),
                value: Type.Any(),
                createdAt: Type.String()
            })),
            status: Type.Union([
                Type.Literal('default-only'),
                Type.Literal('unpublished'),
                Type.Literal('published'),
                Type.Literal('modified')
            ])
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * GET /_cms/labels/:id/versions - 라벨 버전 히스토리 조회
 */
export const getLabelVersionsContract = {
    method: 'GET' as const,
    path: '/_cms/labels/:id/versions',
    params: Type.Object({
        id: Type.String({ description: '라벨 ID' })
    }),
    response: Type.Union([
        Type.Object({
            versions: Type.Array(Type.Object({
                version: Type.Number({ description: '버전 번호' }),
                publishedAt: Type.String({ description: '발행 시각 (ISO 8601)' }),
                publishedBy: Type.Union([Type.String(), Type.Null()], { description: '발행자 ID' }),
                notes: Type.Union([Type.String(), Type.Null()], { description: '발행 노트' }),
                values: Type.Array(Type.Object({
                    id: Type.Number(),
                    locale: Type.String(),
                    breakpoint: Type.Union([Type.String(), Type.Null()]),
                    value: Type.Any(),
                    createdAt: Type.String()
                }))
            }))
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;